import React, { useState, useEffect, JSX } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    required,
    NumberInput,
    useNotify,
    useRedirect,
    SelectArrayInput,
    BooleanInput,
    useRecordContext,
    useUpdate,
    WithRecord,
    usePermissions,
    TextField,
    BooleanField,
    Toolbar,
    SaveButton,
    DeleteButton, TabbedForm
} from 'react-admin';
import { WorkflowContainer } from "../workflow/WorkflowContainer";
import {
    Box
} from "@mui/material";
import { permissionManager } from "../permissions/manager";
import '../workflow/styles/workflow-tabs.scss';
import { Workflow } from "../../types/entities";
import { ChatFieldBridge } from "../chat-bot/components/ChatFieldBridge";

const ToolBar: () => JSX.Element = (): JSX.Element => {
    const { permissions } = usePermissions();
    return (
        <Toolbar sx={ { display: 'flex', justifyContent: 'space-between' } }>
            {/* Save is checked by hand because `SaveButton` is the one control here with no
                `canAccess` of its own. `DeleteButton` has one, so it is left bare - a check beside
                it would be the same rule written twice, and the two copies would drift.

                `.edit` rather than the section id: a role holding the workflow verbs without a
                grant on the whole section would otherwise lose Save, which is exactly the shape
                per-verb permissions exist to allow. */}
            { permissionManager.isAllowed(permissions, 'HQ_System::workflows.edit') &&
                <SaveButton />
            }
            <DeleteButton />
        </Toolbar>
    );
}

export const WorkflowEdit: () => JSX.Element = (): JSX.Element => {
    const [update, { isPending }] = useUpdate();
    const { permissions } = usePermissions();

    function handleFormSubmit(data: any, record: any) {
        if (typeof data.configuration === 'object') {
            data.configuration = JSON.stringify(data.configuration);
        }
        const today = new Date();
        let dateString: string = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
        data.createdAt = dateString;
        data.updatedAt = dateString;
        update(
            'workflows',
            { id: data.id, data: data, previousData: record },
            {
                onSuccess: (): void => {
                    if (window !== undefined && window.localStorage.getItem('create_node_temporary_store_edit')) {
                        window.localStorage.removeItem('create_node_temporary_store_edit');
                    }
                },
            }
        );
    }

    return (
        <Edit queryOptions={{
            refetchInterval: false,
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
        }} >

            <WithRecord
                label=""
                render={
                    (record: Workflow): JSX.Element => {
                        return (
                            <TabbedForm toolbar={ <ToolBar /> }
                                        onSubmit={ (e: any): void =>  handleFormSubmit(e, record) } >
                                <TabbedForm.Tab label="Workflow">
                                    <WorkflowContainer mode="edit" />
                                    {/* Inside a tab, not beside them: TabbedFormTabs clones every direct child into the
                                        tab bar. One tab is enough - react-admin mounts them all and hides the
                                        inactive ones, so the whole form is registered from here. */}
                                    <ChatFieldBridge />
                                </TabbedForm.Tab>
                                <TabbedForm.Tab label="Edit">
                                    <TextInput name="configuration" source="configuration" fullWidth sx={{
                                        display: 'none'
                                    }} />
                                    <TextInput name="name" source="name" validate={[required()]} fullWidth />
                                    { (!permissionManager.isAllowed(permissions, 'HQ_System::workflows.edit')) &&
                                        <Box sx={ { width: '100%', display: 'flex', marginBottom: '16px', alignItems: 'center' } } >
                                            <span style={ { marginRight: '20px' } } >{ "System Code: " }</span>
                                            <TextField label="System Code" source="systemCode" fullWidth />
                                        </Box>

                                    }
                                    { (permissionManager.isAllowed(permissions, 'HQ_System::workflows.edit')) &&
                                        <TextInput name="systemCode" source="systemCode" fullWidth />
                                    }
                                    { (permissionManager.isAllowed(permissions, 'HQ_System::workflows.edit')) &&
                                        <>
                                            <BooleanInput name="enabled" source="enabled" validate={[required()]} />
                                            <BooleanInput name="isSystem" source="isSystem" validate={[required()]} />
                                        </>
                                    }
                                    { (!permissionManager.isAllowed(permissions, 'HQ_System::workflows.edit')) &&
                                        <>
                                            <Box sx={ { width: '100%', display: 'flex', marginBottom: '16px', alignItems: 'center' } } >
                                                <span style={ { marginRight: '20px' } } >{ "Enabled: " }</span>
                                                <BooleanField source="enabled" />
                                            </Box>
                                            <Box sx={ { width: '100%', display: 'flex', marginBottom: '16px', alignItems: 'center' } } >
                                                <span style={ { marginRight: '20px' } } >{ "Is system: " }</span>
                                                <BooleanField source="isSystem" />
                                            </Box>
                                        </>
                                    }
                                </TabbedForm.Tab>
                            </TabbedForm>
                        )
                    }
                }
            />

        </Edit>
    );
}
